/**
 * leadform_ink1 (2026-10-18 · Node 11:48 「Ink & White」) — 관심고객 폼 한 벌의 «문구·요약·전송 묶음» 순수 함수.
 *
 * ⚠️ 화면(LeadForm.tsx)은 'use client' 다. 판정·문구·payload 조립을 여기 두면
 *    서버 페이지(요약 3칸 재료)와 테스트(payload 모양)가 같은 함수를 부른다 — 두 벌이 생기지 않는다.
 * ⚠️ 입력 칸은 이름·연락처·생년월일 3개(허브는 생년월일 없음 — 종전 규칙). «희망 타입» 칸은 화면에서 뺐고
 *    저장 호환을 위해 desiredType 은 「미정」 고정으로 보낸다(leads.desired_type · 시트 열 그대로).
 * ⛔ 「광고 전화 없음」 같은 약속 문구를 만들지 않는다. 확정 전 내용을 확정처럼 쓰지 않는다.
 */
import { CODE_FLAG_DEFAULTS } from '@/lib/config/flag-defaults';
import { leadCopyKind, type LeadCopy } from '@/lib/apt/lead-copy';
import { daysBetween, normDate } from '@/lib/apt/schedule';
import { nameWithoutNumbers, numericTokens } from '@/lib/cl-core/numbers';

/** 희망 타입 저장값 — 화면 칸은 없고 이 값으로 고정 전송한다. */
export const LEAD_DESIRED_TYPE_FIXED = '미정';

export type LeadVariant = 'detail' | 'blog' | 'home' | 'region';

/** 문구 계열. sale = 분양(공고 전 비정비 현장 포함) · redev = 공고 전 정비 · resale = 기축 매물 · existing = 입주 끝난 단지(같은 지역 분양) */
export type LeadInkKind = 'sale' | 'redev' | 'resale' | 'existing' | 'redev_hub' | 'area_hub' | 'home';

export function leadInkKind(input: {
  variant: LeadVariant; lifecycleStage?: string | null; isRedev?: boolean; redevInProgress?: boolean; copy: LeadCopy;
}): LeadInkKind {
  if (input.variant === 'home') return 'home';
  // 허브 두 계열 — lead-copy 가 고른 inquiryType 이 이미 말한다(redev 허브만 '진행상황알림').
  if (input.variant === 'region') return input.copy.inquiryType === '진행상황알림' ? 'redev_hub' : 'area_hub';
  const k = leadCopyKind(input.lifecycleStage, { redevInProgress: input.redevInProgress });
  if (k === 'resale') return 'resale';
  if (k === 'existing') return 'existing';
  // 공고 전: 정비(모르면 정비판 — lead-copy 와 같은 기본값)만 「분양」 말을 쓰지 않는다.
  if (k === 'pre_notice' && input.isRedev !== false) return 'redev';
  return 'sale';
}

export interface LeadInkCopy {
  /** 눈썹 글 — 「분양 알림 · 청약 D-n」 */
  eyebrow: string;
  /** 제목 1줄(단지명) */
  titleName: string;
  /** 제목 2줄(400 회색) */
  titleSub: string;
  /** 아래 안내 1줄 */
  note: string;
  /** 아래 안내 2줄 */
  note2: string;
  /** 바텀시트 제목 */
  sheetTitle: string;
  /** 완료 문장 — 마스킹 번호를 받는다 */
  doneLine: (masked: string) => string;
}

const NOTE2 = '확정 전 내용은 확정으로 안내하지 않습니다';

/** D-n 표기 — 0 은 D-day. 음수·null 은 표기 없음. */
export function ddayText(dday: number | null | undefined): string | null {
  if (dday == null || !Number.isFinite(dday) || dday < 0) return null;
  return dday === 0 ? 'D-day' : `D-${dday}`;
}

export function leadInkCopy(input: {
  kind: LeadInkKind; copy: LeadCopy; siteName?: string; regionName?: string; dday?: number | null;
}): LeadInkCopy {
  const { kind, copy } = input;
  // CL5 — 수치 토큰 든 이름(「10년」 등)은 h1 밖에서 수치를 걷는다(상세 h2·의도 이름과 같은 규칙).
  const siteRaw = String(input.siteName ?? '').trim();
  const site = numericTokens(siteRaw).length ? nameWithoutNumbers(siteRaw) || siteRaw : siteRaw;
  const place = String(input.regionName ?? '').trim();
  const band = copy.band.replace(/\s*·\s*무료\s*$/, '').trim();
  const d = ddayText(input.dday);
  const eyebrow = kind === 'sale' ? (d ? `분양 알림 · 청약 ${d}` : '분양 알림') : kind === 'home' ? '분양 알림' : band;
  // #4b(CL12) — 허브 폼 제목·첫 문장은 rank_lock 보존 문장(「{지역} 분양·미분양 안내 신청 이 지역에 새로 나오는 …」)이다 → 허브는 종전 copy.cta·copy.lede 그대로.
  const isHub = kind === 'redev_hub' || kind === 'area_hub';
  const titleName = kind === 'home' ? '관심 현장' : isHub ? copy.cta : (site || copy.cta);
  // L1 D4 — 「매물상담」 폐지: 기축(resale)은 같은 지역 분양·입주 소식 알림이다(매물·시세·담당자 연락 어휘 없음).
  const titleSub = kind === 'resale' ? '같은 지역 분양·입주 소식을 받아보세요' : isHub ? copy.lede : '소식을 가장 먼저 받아보세요';
  const note = ({
    sale: '입력 정보는 이 현장 분양 소식 안내에만 사용합니다',
    redev: '입력 정보는 이 구역 진행 소식 안내에만 사용합니다',
    resale: '입력 정보는 같은 지역 분양·입주 소식 안내에만 사용합니다',
    existing: '입력 정보는 같은 지역 분양 소식 안내에만 사용합니다',
    redev_hub: `입력 정보는 ${place ? `${place} ` : ''}구역 진행 소식 안내에만 사용합니다`,
    area_hub: `입력 정보는 ${place ? `${place} ` : ''}분양 소식 안내에만 사용합니다`,
    home: '입력 정보는 분양 소식 안내에만 사용합니다',
  } as const)[kind];
  const doneLine = (m: string) => ({
    sale: `분양가·일정이 확정되면 ${m} 로 알려드릴게요`,
    redev: `다음 단계 소식이 나오면 ${m} 로 알려드릴게요`,
    resale: `새 분양·입주 소식이 나오면 ${m} 로 알려드릴게요`,
    existing: `새 분양 소식이 나오면 ${m} 로 알려드릴게요`,
    redev_hub: `구역 소식이 나오면 ${m} 로 알려드릴게요`,
    area_hub: `새 분양 소식이 나오면 ${m} 로 알려드릴게요`,
    home: `새 분양 소식이 나오면 ${m} 로 알려드릴게요`,
  } as const)[kind];
  return { eyebrow, titleName, titleSub, note, note2: NOTE2, sheetTitle: `${isHub ? (place || copy.cta) : titleName} 알림 받기`, doneLine };
}

/** 휴대폰 번호 → 「앞 3자리-****-뒤 4자리」 (뒤 4자리만 보인다). 자릿수가 모자라면 끝 4자리만 살린다. */
export function maskPhone(phone: string): string {
  const d = String(phone ?? '').replace(/[^0-9]/g, '');
  if (d.length < 4) return '***';
  const head = d.length >= 10 ? d.slice(0, 3) : '';
  return head ? `${head}-****-${d.slice(-4)}` : `****-${d.slice(-4)}`;
}

/* ───────── 일정 요약 3칸 ───────── */

export interface LeadSummaryCell { key: 'price' | 'special' | 'winner'; label: string; value: string }
export interface LeadSummary {
  /** 청약 접수 시작까지 남은 날(0 = 접수 중 또는 당일). 없으면 null. */
  dday: number | null;
  /** 2칸 이상일 때만 채운다(1칸 이하면 빈 배열 — 줄 전체 생략). */
  cells: LeadSummaryCell[];
}

const WD = ['일', '월', '화', '수', '목', '금', '토'];
/** YYYY-MM-DD → 「10.20(화)」 */
export function shortDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const wd = WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${m}.${d}(${wd})`;
}

/**
 * 요약 3칸 재료. 서버 페이지가 부른다(사전에 가린 분양가만 price 로 넘긴다 — 결정 8·출처 없는 분양가는 이미 비어 있다).
 *  · 분양가: 확정 금액(price) → 그 값 / 공고 전(sale 계열) → 「확정 즉시」 / 그 밖 → 칸 없음.
 *  · 특별공급 접수일 · 당첨 발표일: 공고 날짜가 있을 때만.
 *  · 2칸 미만이면 cells = [] (분양예정·정비는 고정 문구 대신 아예 생략).
 */
export function leadSummaryOf(input: {
  sub: { announcement_date?: string | null; spsply_rcept_bgnde?: string | null; spsply_rcept_endde?: string | null; rcept_bgnde?: string | null; rcept_endde?: string | null; przwner_presnatn_de?: string | null } | null | undefined;
  today: string;
  /** 확정 분양가 표기(이미 가림 처리된 값). 없으면 null. */
  price?: string | null;
  /** 분양 계열(sale)인가 — 「확정 즉시」는 이 계열의 공고 전에만. */
  sale: boolean;
}): LeadSummary {
  const sub = input.sub ?? null;
  const ann = normDate(sub?.announcement_date ?? null);
  const spB = normDate(sub?.spsply_rcept_bgnde ?? null);
  const spE = normDate(sub?.spsply_rcept_endde ?? null);
  const rB = normDate(sub?.rcept_bgnde ?? null);
  const rE = normDate(sub?.rcept_endde ?? null);
  const win = normDate(sub?.przwner_presnatn_de ?? null);
  const cells: LeadSummaryCell[] = [];
  const price = String(input.price ?? '').trim();
  if (price) cells.push({ key: 'price', label: '분양가', value: price });
  else if (input.sale && !ann && !spB && !rB) cells.push({ key: 'price', label: '분양가', value: '확정 즉시' });
  if (spB) cells.push({ key: 'special', label: '특별공급 접수일', value: shortDay(spB) });
  if (win) cells.push({ key: 'winner', label: '당첨 발표일', value: shortDay(win) });

  let dday: number | null = null;
  if (input.sale) {
    for (const [b, e] of [[spB, spE], [rB, rE]] as const) {
      if (!b) continue;
      const toStart = daysBetween(input.today, b);
      const toEnd = daysBetween(input.today, e ?? b);
      const n = toStart >= 0 ? toStart : toEnd >= 0 ? 0 : null;
      if (n != null && (dday == null || n < dday)) dday = n;
    }
  }
  return { dday, cells: cells.length >= 2 ? cells : [] };
}

/* ───────── 동의 서식 B(P6 간소화) ─────────
 * 관심고객 폼의 동의는 체크 2칸 — ① [필수] 개인정보 수집·이용(처리위탁·14세 고지 포함, 「자세히」 펼침) · ② [선택] 분양·입주 소식 받기.
 * ②는 문자·전화·카카오톡 3매체 전부 + 다른 분양 현장 안내(홍보·판매 권유)를 한 문장으로 묶는다. 야간 수신 칸은 없다(야간에는 보내지 않는다).
 * 문구·버전·해시는 여기 한 벌 — 폼(화면)·payload·테스트가 같은 상수를 읽는다.
 * ⚠️ 문구를 고치면 LEAD_CONSENT_VERSION 을 올리고 LEAD_CONSENT_TEXT_HASH 를 다시 계산한다
 *    (테스트 l1-consent 가 node:crypto sha256 으로 해시를 잠근다 — 둘이 어긋나면 실패한다).
 * ⛔ 「제3자 제공」「홍보 위탁」 문구를 만들지 않는다 — 결정 D1: 리드는 카더라 운영자만 본다. */

/** 광고성 정보 수신 매체 — 값은 leads.consent_channels 에 그대로 실린다. 선택 동의 한 칸이 셋 전부를 켠다. */
export const LEAD_CONSENT_CHANNELS = [
  { key: 'sms', label: '문자' },
  { key: 'call', label: '전화' },
  { key: 'kakao', label: '카카오톡' },
] as const;
export type LeadConsentChannel = (typeof LEAD_CONSENT_CHANNELS)[number]['key'];

/** 동의 서식 판 — leads.consent_version. 문구가 바뀌면 올린다(a1 = 서식 A 7칸 · b1 = 서식 B 2칸). */
export const LEAD_CONSENT_VERSION = 'lead-consent-b1';

/** [필수] 개인정보 수집·이용 — 체크 한 칸 + 「자세히」 펼침 고지(수집 항목·목적·보유기간·처리위탁·14세·거부 권리와 불이익). 전문은 /privacy. */
export const LEAD_CONSENT_REQUIRED = {
  title: '개인정보 수집·이용 동의',
  details: [
    '수집 항목: 이름·휴대폰 번호·생년월일(선택)·관심 현장·접속 경로',
    '이용 목적: 신청 현장 분양·진행 소식 안내, 상담 연락',
    '보유 기간: 수집일로부터 6개월 후 파기',
    '처리위탁: Google LLC(미국, 신청 내역 저장)',
    '만 14세 이상만 신청할 수 있습니다',
    '동의를 거부할 권리가 있으며, 거부하면 소식 알림 신청을 할 수 없습니다',
  ],
} as const;

/** [선택] 분양·입주 소식 받기 — 광고성 정보 수신(정보통신망법 제50조) 3매체 + 홍보·판매 권유 목적 처리를 한 문장으로. */
export const LEAD_CONSENT_OPTIONAL = {
  title: '분양·입주 소식 받기(문자·전화·카카오톡)',
  law: '정보통신망법 제50조',
  /** 현장명이 없으면(홈·허브) 「신청 현장」으로 부른다. */
  line: (siteName: string) => `${(siteName || '').trim() || '신청 현장'} 및 다른 분양 현장의 분양·입주 소식(광고성 정보)을 문자·전화·카카오톡으로 받고, 상담 권유에 이용하는 데 동의합니다. 야간(21~08시)에는 보내지 않습니다`,
  refuse: '거부해도 신청 현장 안내는 받을 수 있습니다. 수신거부는 받은 문자·카카오톡의 안내 또는 아래 연락처로 언제든 할 수 있고, 수신 의사는 2년마다 다시 확인합니다',
} as const;

/** 동의 전문(해시 대상) — 화면에 보이는 고지 문구를 줄바꿈으로 이은 고정 문자열(현장명 자리는 자리표시). */
export const LEAD_CONSENT_TEXT = [
  `[필수] ${LEAD_CONSENT_REQUIRED.title}`,
  ...LEAD_CONSENT_REQUIRED.details,
  `[선택] ${LEAD_CONSENT_OPTIONAL.title}(${LEAD_CONSENT_OPTIONAL.law})`,
  LEAD_CONSENT_OPTIONAL.line('{현장명}'),
  LEAD_CONSENT_OPTIONAL.refuse,
].join('\n');

/** sha256(LEAD_CONSENT_TEXT) hex — 브라우저에서 계산하지 않고 상수로 싣는다(테스트가 잠근다). */
export const LEAD_CONSENT_TEXT_HASH = 'ccc7e5007c3b586d7b8896477cc74edecc46b0aabe7ef10006bf3580e1b6af0a';

/** 폼 상태 → 저장할 동의 값. 선택 동의 한 칸이 참이면 3매체 전부 · 홍보 권유 참. 야간은 항상 거짓(받지 않는다). */
export function resolveLeadConsent(input: { optionalOk: boolean }): {
  marketingOk: boolean; consentChannels: LeadConsentChannel[]; nightOk: boolean; marketingPromoOk: boolean;
} {
  const on = !!input.optionalOk;
  return {
    marketingOk: on,
    consentChannels: on ? LEAD_CONSENT_CHANNELS.map((c) => c.key) : [],
    nightOk: false,
    marketingPromoOk: on,
  };
}

/* ───────── 전송 묶음 ───────── */

export interface LeadPayload {
  name: string;
  phone: string;
  birthDate: string;
  desiredType: string;
  consent: boolean;
  marketingOk: boolean;
  dwellMs: number;
  siteSlug: string;
  siteName: string;
  region: string;
  sigungu: string;
  interestRegion: string;
  siteUrl: string;
  inquiryType: string;
  entryPath: string;
  sourceDomain: string;
  channel?: string;
  company: string;
  query: Record<string, string>;
  leadRef: string;
  /* L1 동의 기록 — Apps Script 가 모르는 필드는 무시한다(fn_insert_lead 새 인자는 DEFAULT NULL). 끝에만 «더한다». */
  /** 동의 체크 뒤 제출 버튼을 누른 시각(클라이언트 ISO). */
  consentedAt: string;
  consentVersion: string;
  consentTextHash: string;
  /** 광고성 정보 수신 매체(sms·call·kakao). 수신 동의가 없으면 []. */
  consentChannels: string[];
  /** 야간(21~08시) 수신 — 서식 B 는 야간 칸이 없어 항상 false(필드는 Apps Script·DB 호환으로 유지). */
  nightOk: boolean;
  /** 동의를 받은 화면의 URL path. */
  consentSource: string;
  /** 홍보·판매 권유 목적 처리(다른 분양 현장 안내) 동의. */
  marketingPromoOk: boolean;
}

/**
 * retention-1031 — 생년월일은 «선택» 이다(시작 161 → 제출 49, 70% 이탈 · 상세 폼 3칸 중 유일한 주민 정보).
 *   하류 확인: Apps Script(시트 leads 12열)·leads.birth_date 는 빈 값을 받는다 — 허브 폼이 9/23 부터 빈 값을 보내 왔고
 *   최근 60일 leads 6건이 birth_date NULL 로 정상 적재·시트 기록됐다. 노본 영업 시트는 지역·현장으로 나누며 생년월일을 쓰지 않는다.
 *   ⚠️ Node 9/26 「생년월일 필수 유지」 를 뒤집는 값이다 — 되돌리려면 이 상수 하나만 true 로.
 */
export const LEAD_BIRTH_REQUIRED = CODE_FLAG_DEFAULTS.lead_birth_required;

/** 순수 — 생년월일 칸 오류 문구(없으면 null). 필수가 아니면 «비었거나 6자리» 면 통과. */
export function leadBirthError(raw: string, required: boolean = LEAD_BIRTH_REQUIRED): string | null {
  const d = String(raw ?? '').replace(/\D/g, '');
  if (required) return d.length === 6 ? null : '생년월일 6자리를 입력해 주세요';
  return d.length === 0 || d.length === 6 ? null : '생년월일은 6자리로 입력하거나 비워 두세요';
}

/**
 * payload 조립 — 종전 LeadForm.handleSubmit 안의 객체 리터럴을 «바이트 의미 그대로» 옮겼다.
 * 바뀐 것은 desiredType 하나(화면 칸 제거 → 「미정」 고정). 키 집합·순서·channel(홈만)·허니팟(company)·query 는 그대로다.
 */
export function buildLeadPayload(input: {
  name: string; phone: string; birthDate: string; consent: boolean; marketingOk: boolean; dwellMs: number;
  siteSlug: string; siteName: string; regionText: string; sigunguText: string; siteUrl: string; inquiryType: string;
  entryPath: string; sourceDomain: string; isHome: boolean; company: string; query: Record<string, string>; leadRef: string;
  /** L1 동의 기록. 안 주면(옛 호출) 빈 값 — 매체 []·야간 false·홍보 false. */
  consent2?: { consentedAt: string; consentChannels: readonly string[]; nightOk: boolean; consentSource: string; marketingPromoOk: boolean };
}): LeadPayload {
  const c2 = input.consent2;
  return {
    name: input.name,
    phone: input.phone, // 원본 그대로. 정규화는 서버가 한다
    birthDate: input.birthDate,
    desiredType: LEAD_DESIRED_TYPE_FIXED,
    consent: input.consent,
    marketingOk: input.marketingOk,
    dwellMs: input.dwellMs, // 3초 미만 판정은 서버가 한다 — 여기서 막지 않는다
    siteSlug: input.siteSlug,
    siteName: input.siteName,
    region: input.regionText,
    sigungu: input.sigunguText,
    interestRegion: [input.regionText, input.sigunguText].filter(Boolean).join(' '),
    siteUrl: input.siteUrl,
    inquiryType: input.inquiryType,
    entryPath: input.entryPath,
    sourceDomain: input.sourceDomain,
    ...(input.isHome ? { channel: 'home' } : {}),
    company: input.company,
    query: input.query,
    leadRef: input.leadRef,
    consentedAt: c2?.consentedAt ?? '',
    consentVersion: LEAD_CONSENT_VERSION,
    consentTextHash: LEAD_CONSENT_TEXT_HASH,
    consentChannels: [...(c2?.consentChannels ?? [])],
    nightOk: !!c2?.nightOk,
    consentSource: c2?.consentSource ?? input.entryPath,
    marketingPromoOk: !!c2?.marketingPromoOk,
  };
}
